// src/components/AlignDialog.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  IconButton,
} from "@mui/material";
import {
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  AlignHorizontalRight,
  AlignVerticalTop,
  AlignVerticalCenter,
  AlignVerticalBottom,
} from "@mui/icons-material";
import { DraggablePaper } from "./DraggablePaper";
import { ActiveSelection } from "fabric";

const AlignDialog = React.memo(({ open, onClose, canvas }) => {
  const [isDirty, setIsDirty] = useState(false);
  const originalPositionsRef = useRef([]);
  const canvasRef = useRef(canvas);

  // Update canvas ref
  useEffect(() => {
    canvasRef.current = canvas;
    console.log("[AlignDialog] Updated canvasRef:", canvas);
  }, [canvas]);

  // Initialize original positions when dialog opens
  useEffect(() => {
    if (open && canvasRef.current) {
      const activeSelection = canvasRef.current.getActiveObject();
      console.log(
        "[AlignDialog] Initializing: activeSelection =",
        activeSelection
      );
      console.log(
        "[AlignDialog] Initializing: activeSelection.type =",
        activeSelection?.type
      );
      console.log(
        "[AlignDialog] Initializing: activeSelection.type (lowercase) =",
        activeSelection?.type?.toLowerCase()
      );
      console.log(
        "[AlignDialog] Initializing: is ActiveSelection =",
        activeSelection instanceof ActiveSelection
      );
      if (
        activeSelection &&
        (activeSelection.type?.toLowerCase() === "activeselection" ||
          activeSelection instanceof ActiveSelection)
      ) {
        originalPositionsRef.current = activeSelection._objects.map((obj) => ({
          left: obj.left,
          top: obj.top,
        }));
        setIsDirty(false);
        console.log(
          "[AlignDialog] Initialized original positions:",
          originalPositionsRef.current
        );
      } else {
        console.log(
          "[AlignDialog] Initialization skipped: Invalid activeSelection",
          {
            hasActiveSelection: !!activeSelection,
            type: activeSelection?.type,
            typeLowercase: activeSelection?.type?.toLowerCase(),
            isActiveSelection: activeSelection instanceof ActiveSelection,
            hasObjects: activeSelection?._objects?.length >= 2,
          }
        );
      }
    }
  }, [open]);

  // Alignment function adapted from AlignToolbar
  const alignObjects = useCallback((direction) => {
    const activeSelection = canvasRef.current?.getActiveObject();
    console.log(
      "[AlignDialog] alignObjects: activeSelection =",
      activeSelection
    );
    console.log(
      "[AlignDialog] alignObjects: activeSelection.type =",
      activeSelection?.type
    );
    console.log(
      "[AlignDialog] alignObjects: activeSelection.type (lowercase) =",
      activeSelection?.type?.toLowerCase()
    );
    console.log(
      "[AlignDialog] alignObjects: is ActiveSelection =",
      activeSelection instanceof ActiveSelection
    );
    console.log("[AlignDialog] alignObjects: canvas =", canvasRef.current);
    console.log("[AlignDialog] alignObjects: direction =", direction);

    if (
      !canvasRef.current ||
      !activeSelection ||
      !(
        activeSelection.type?.toLowerCase() === "activeselection" ||
        activeSelection instanceof ActiveSelection
      ) ||
      !activeSelection._objects ||
      activeSelection._objects.length < 2
    ) {
      console.log("[AlignDialog] alignObjects: Invalid selection or canvas", {
        hasCanvas: !!canvasRef.current,
        hasActiveSelection: !!activeSelection,
        type: activeSelection?.type,
        typeLowercase: activeSelection?.type?.toLowerCase(),
        isActiveSelection: activeSelection instanceof ActiveSelection,
        hasObjects: activeSelection?._objects?.length >= 2,
      });
      // Attempt to recreate selection
      const objects = canvasRef.current.getObjects().slice(0, 2);
      if (objects.length >= 2) {
        console.log("[AlignDialog] alignObjects: Recreating ActiveSelection");
        const newSelection = new ActiveSelection(objects, {
          canvas: canvasRef.current,
        });
        canvasRef.current.setActiveObject(newSelection);
        canvasRef.current.requestRenderAll();
        return alignObjects(direction); // Retry with new selection
      }
      return;
    }

    // Get the initial bounding box (excluding stroke/shadow and padding)
    const bound = activeSelection.getBoundingRect(true, true);
    const zoom = canvasRef.current.getZoom() || 1;

    // Get the ActiveSelection's group offset
    const groupLeft = activeSelection.left;
    const groupTop = activeSelection.top;

    // Use bounding box coordinates in object space
    const boundLeft = bound.left;
    const boundTop = bound.top;
    const boundWidth = bound.width;
    const boundHeight = bound.height;

    console.log("[AlignDialog] Aligning objects", {
      direction,
      bound: {
        left: boundLeft,
        top: boundTop,
        width: boundWidth,
        height: boundHeight,
      },
      groupOffset: { left: groupLeft, top: groupTop },
      objectCount: activeSelection._objects.length,
      zoom,
    });

    activeSelection.forEachObject((obj) => {
      // Get object’s bounding rect (excluding stroke/shadow and padding)
      const objBound = obj.getBoundingRect(true, true);
      const objWidth = objBound.width;
      const objHeight = objBound.height;

      // Objects have originX: "center" and originY: "center"
      // Calculate current canvas-space positions (before alignment)
      const currentCanvasLeft = obj.left + groupLeft;
      const currentCanvasTop = obj.top + groupTop;

      let newCanvasLeft, newCanvasTop;

      // Calculate new positions in canvas space, aligning edges to bounding box
      switch (direction) {
        case "left":
          newCanvasLeft = boundLeft; // Align left edge to bound.left
          newCanvasTop = currentCanvasTop; // Preserve vertical position
          break;
        case "center":
          newCanvasLeft = boundLeft + boundWidth / 2; // Align center to bound center
          newCanvasTop = currentCanvasTop;
          break;
        case "right":
          newCanvasLeft = boundLeft + boundWidth - objWidth; // Align right edge to bound.right
          newCanvasTop = currentCanvasTop;
          break;
        case "top":
          newCanvasLeft = currentCanvasLeft; // Preserve horizontal position
          newCanvasTop = boundTop; // Align top edge to bound.top
          break;
        case "middle":
          newCanvasLeft = currentCanvasLeft;
          newCanvasTop = boundTop + boundHeight / 2; // Align vertical center to bound middle
          break;
        case "bottom":
          newCanvasLeft = currentCanvasLeft;
          newCanvasTop = boundTop + boundHeight - objHeight; // Align bottom edge to bound.bottom
          break;
        default:
          console.warn("[AlignDialog] Unknown direction", { direction });
          return;
      }

      // Adjust for originX: "center" and originY: "center"
      // Since left/top are the center, adjust to align the edge
      const adjustedCanvasLeft = newCanvasLeft + objWidth / 2;
      const adjustedCanvasTop = newCanvasTop + objHeight / 2;

      // Convert back to group coordinates
      const newLeft = adjustedCanvasLeft - groupLeft;
      const newTop = adjustedCanvasTop - groupTop;

      console.log("[AlignDialog] Object alignment", {
        type: obj.type,
        originalLeft: obj.left,
        originalTop: obj.top,
        originalCanvasLeft: currentCanvasLeft,
        originalCanvasTop: currentCanvasTop,
        newCanvasLeft: adjustedCanvasLeft,
        newCanvasTop: adjustedCanvasTop,
        newLeft,
        newTop,
        objWidth,
        objHeight,
        originX: obj.originX,
        originY: obj.originY,
      });

      // Set new position
      obj.set({ left: newLeft, top: newTop });

      // Update coordinates
      obj.setCoords();
    });

    // Update the ActiveSelection and canvas
    activeSelection.setCoords();
    canvasRef.current.requestRenderAll();

    // Deselect all objects
    canvasRef.current.discardActiveObject();
    canvasRef.current.requestRenderAll();

    // Mark as dirty to enable Apply button
    setIsDirty(true);

    console.log("[AlignDialog] Alignment complete and objects deselected", {
      direction,
    });
  }, []);

  const alignLeft = useCallback(() => alignObjects("left"), [alignObjects]);
  const alignCenter = useCallback(() => alignObjects("center"), [alignObjects]);
  const alignRight = useCallback(() => alignObjects("right"), [alignObjects]);
  const alignTop = useCallback(() => alignObjects("top"), [alignObjects]);
  const alignMiddle = useCallback(() => alignObjects("middle"), [alignObjects]);
  const alignBottom = useCallback(() => alignObjects("bottom"), [alignObjects]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (isDirty && canvasRef.current) {
      const activeSelection = canvasRef.current.getActiveObject();
      if (activeSelection && activeSelection._objects) {
        activeSelection._objects.forEach((obj, index) => {
          const original = originalPositionsRef.current[index];
          if (original) {
            obj.set({ left: original.left, top: original.top });
            obj.setCoords();
          }
        });
        canvasRef.current.requestRenderAll();
        console.log("[AlignDialog] Canceled, reverted to original positions");
      } else {
        // If objects are deselected, reselect them to revert positions
        const objects = canvasRef.current.getObjects();
        const matchingObjects = objects.filter((obj, index) => {
          const original = originalPositionsRef.current[index];
          return (
            original && obj.left === original.left && obj.top === original.top
          );
        });
        if (matchingObjects.length === originalPositionsRef.current.length) {
          const newSelection = new ActiveSelection(matchingObjects, {
            canvas: canvasRef.current,
          });
          matchingObjects.forEach((obj, index) => {
            const original = originalPositionsRef.current[index];
            if (original) {
              obj.set({ left: original.left, top: original.top });
              obj.setCoords();
            }
          });
          canvasRef.current.setActiveObject(newSelection);
          canvasRef.current.requestRenderAll();
          console.log(
            "[AlignDialog] Canceled, recreated selection and reverted positions"
          );
        }
      }
    }
    onClose();
  }, [isDirty, onClose]);

  // Handle apply
  const handleApply = useCallback(() => {
    if (isDirty && canvasRef.current) {
      const objects = canvasRef.current.getObjects();
      const matchingObjects = objects.filter((obj, index) => {
        const original = originalPositionsRef.current[index];
        return (
          original && obj.left === original.left && obj.top === original.top
        );
      });
      if (matchingObjects.length === originalPositionsRef.current.length) {
        const newSelection = new ActiveSelection(matchingObjects, {
          canvas: canvasRef.current,
        });
        canvasRef.current.setActiveObject(newSelection);
        newSelection._objects.forEach((obj) => {
          obj.setCoords();
        });
        canvasRef.current.requestRenderAll();
        canvasRef.current.fire("object:modified", { target: newSelection });
        console.log(
          "[AlignDialog] Applied alignments, fired object:modified for recreated selection:",
          newSelection
        );
      } else {
        console.warn(
          "[AlignDialog] Apply: Could not recreate selection for object:modified"
        );
      }
    } else {
      console.log(
        "[AlignDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvasRef.current
      );
    }
    setIsDirty(false);
    onClose();
  }, [isDirty, onClose]);

  // Event propagation handlers
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  console.log("[AlignDialog] Render: canvas =", canvas);
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Align Toolbar
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ marginTop: 1 }}>
          <Stack direction="row" spacing={1} justifyContent="center">
            <IconButton
              onClick={() => {
                console.log("[AlignDialog] Clicked Align Left");
                alignLeft();
              }}
              aria-label="Align objects left"
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}
            >
              <AlignHorizontalLeft />
            </IconButton>
            <IconButton
              onClick={() => {
                console.log("[AlignDialog] Clicked Align Center");
                alignCenter();
              }}
              aria-label="Align objects center"
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}
            >
              <AlignHorizontalCenter />
            </IconButton>
            <IconButton
              onClick={() => {
                console.log("[AlignDialog] Clicked Align Right");
                alignRight();
              }}
              aria-label="Align objects right"
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}
            >
              <AlignHorizontalRight />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="center">
            <IconButton
              onClick={() => {
                console.log("[AlignDialog] Clicked Align Top");
                alignTop();
              }}
              aria-label="Align objects top"
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}
            >
              <AlignVerticalTop />
            </IconButton>
            <IconButton
              onClick={() => {
                console.log("[AlignDialog] Clicked Align Middle");
                alignMiddle();
              }}
              aria-label="Align objects middle"
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}
            >
              <AlignVerticalCenter />
            </IconButton>
            <IconButton
              onClick={() => {
                console.log("[AlignDialog] Clicked Align Bottom");
                alignBottom();
              }}
              aria-label="Align objects bottom"
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}
            >
              <AlignVerticalBottom />
            </IconButton>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel align changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply align changes"
          disabled={!isDirty}
        >
          Apply Align
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default AlignDialog;
